import { useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useBrainStore } from '@/state/brainStore';
import { useSessionStore } from '@/state/sessionStore';
import { computeBookingFlags, medicalIsEmpty } from '@/domain/bookingFlags';
import { findPersonaCard } from '@/data/personas';
import MedicalProfileModal from '@/components/MedicalProfileModal';
import CustomerRecordHeader from '@/components/CustomerRecordHeader';
import ConversationThread from '@/components/ConversationThread';
import CustomerComposer from '@/components/CustomerComposer';
import CustomerActionModals, {
  type CustomerAction,
} from '@/components/CustomerActionModals';
import LiveCallBanner from '@/components/LiveCallBanner';
import BackLink from '@/components/shell/BackLink';
import PhotoGallery from '@/components/PhotoGallery';
import EditBookingModal from '@/components/EditBookingModal';
import SlotDepositPane from '@/components/SlotDepositPane';
import CustomerBriefControls from '@/components/CustomerBriefControls';
import WaiverModal from '@/components/WaiverModal';
import { useActiveCall } from '@/hooks/useActiveCall';
import { aud, simTimeFull, customerName, formatRebookOutcome, formatBodyPart } from '@/utils/format';
import { bookingMoney, FLAG_STYLE, lifetimeSpendAud, REFS_LOCATION_LABEL } from '@/utils/bookingSummary';
import DepositMovementDrawer from '@/components/DepositMovementDrawer';
import clsx from 'clsx';
import { lifecycleLabel } from '@atink/domain';
import { depositStatusLabel } from '@/components/customer-card/chips';
import ResendLinkAction from '@/components/deposits/ResendLinkAction';
import { useSimulationStore } from '@/state/simulationStore';
import { bookingPriceAud } from '@/domain/pricing';
import { AWAITING_CLIENT } from '@/domain/selfBookFlow';
import { inspirationFor, progressFor } from '@/utils/imageHelpers';
import { ChevronLeft, Plus, Pencil, ShieldCheck } from 'lucide-react';
import type { CustomerRecord as CustomerRecordType, TailRecord } from '@/domain/types';
import { approvedByOpsLabel, approvedOverrideForBooking } from '@/domain/approvals';

// Promote a TailRecord to a minimal CustomerRecord so the customer screen can
// still render (Customers list pulls from tail records that aren't seeded as
// full records in the brain store).
function tailToCustomer(tail: TailRecord): CustomerRecordType {
  return {
    id: tail.id,
    firstName: tail.firstName,
    lastName: tail.lastName,
    phone: tail.phone,
    ageBand: '25-34',
    suburb: tail.suburb,
    postcode: tail.postcode,
    state: tail.state,
    distanceToNearestStudioKm: tail.distanceFromNearestKm,
    nearestStudioId: tail.primaryStudioId,
    primaryStudioId: tail.primaryStudioId,
    source: { channel: 'sms', firstTouchAt: tail.lastInteractionAt },
    leadScore: 40,
    leadStage: 'lapsed',
    toneTag: 'casual-older',
    engagementStatus: tail.engagementStatus,
    conversation: [
      {
        id: `tail-msg-${tail.id}`,
        customerId: tail.id,
        channel: 'sms',
        direction: 'in',
        body: `Last interaction on file: ${tail.designType.replace('-', ' ')}${tail.hasUnfinishedPiece ? ' (piece unfinished)' : ''}.`,
        timestamp: tail.lastInteractionAt,
        authorRole: 'customer',
      },
    ],
    designIntent: {
      type: tail.designType,
      placement: 'unspecified',
      estimatedHours: 4,
      size: 'half-day',
      referencePhotos: [],
    },
    rapportNotes: [],
    futureReadinessSignals: [],
    bookings: [],
    pieces: [],
    deposits: [],
    creditOnFile: { balanceAud: 0, history: [] },
    crossStudioTouches: [],
    doNotContact: tail.doNotContact,
    lifetimeSpendAud: tail.lastDepositAud ?? 0,
  };
}

export default function CustomerRecord() {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const fromState = location.state as
    | {
        fromCalendarStudioId?: string;
        fromCalendarDayIso?: string;
        handoffFromAI?: boolean;
      }
    | null;
  const storedCustomer = useBrainStore((s) =>
    customerId ? s.customers[customerId] : undefined,
  );
  const tailRecord = useBrainStore((s) =>
    !storedCustomer && customerId
      ? s.tailRecords.find((t) => t.id === customerId)
      : undefined,
  );
  const initialised = useBrainStore((s) => s.initialised);
  const customer = useMemo<CustomerRecordType | undefined>(() => {
    if (storedCustomer) return storedCustomer;
    if (tailRecord) return tailToCustomer(tailRecord);
    return undefined;
  }, [storedCustomer, tailRecord]);

  const bookings = useBrainStore((s) => s.bookings);
  const deposits = useBrainStore((s) => s.deposits);
  const clockIso = useSimulationStore((s) => s.clockIso);
  const waivers = useBrainStore((s) => s.waivers);
  const overrideRequests = useBrainStore((s) => s.overrideRequests);
  const artists = useBrainStore((s) => s.artists);
  const agents = useBrainStore((s) => s.agents);
  const pieces = useBrainStore((s) => s.pieces);
  const salePayments = useBrainStore((s) => s.salePayments);
  const selfBookLinks = useBrainStore((s) => s.selfBookLinks);
  const salePaymentList = useMemo(() => Object.values(salePayments), [salePayments]);
  // Derived from the recorded sales (tv-2na.8.21); never lower than a seeded figure.
  const lifetimeSpend = useMemo(
    () => (customer ? lifetimeSpendAud(customer, bookings, salePaymentList) : 0),
    [customer, bookings, salePaymentList],
  );
  const activeCall = useActiveCall();
  const callForThisCustomer = activeCall?.customerId === customerId ? activeCall : null;
  const [openModal, setOpenModal] = useState<CustomerAction>(null);
  const [editBookingId, setEditBookingId] = useState<string | null>(null);
  const [waiverBookingId, setWaiverBookingId] = useState<string | null>(null);
  const [medicalOpen, setMedicalOpen] = useState(false);

  // Medical data is sensitive: owner + manager (incl. ops lead) edit only.
  // Everyone who can see the record can still read the profile.
  const persona = useSessionStore((s) => s.persona);
  const canEditMedical =
    persona === 'owner' || persona === 'studio-manager' || persona === 'georgina';
  const actorName = persona ? (findPersonaCard(persona)?.name ?? undefined) : undefined;

  if (!customer) {
    // Fixtures load asynchronously on first mount. Show a loading shell rather
    // than a "not found" message until the brain has initialised.
    if (!initialised) {
      return (
        <section className="px-10 py-12" data-testid="customer-loading">
          <p className="font-serif text-cream-soft">Loading record…</p>
        </section>
      );
    }
    return (
      <section className="px-10 py-12">
        <button onClick={() => navigate(-1)} className="btn-quiet">
          <ChevronLeft size={14} /> Back
        </button>
        <p className="font-serif text-cream-soft mt-4">
          {customerId === AWAITING_CLIENT
            ? 'This slot is still awaiting a client. No customer record exists yet.'
            : `Customer not found. ${customerId}.`}
        </p>
      </section>
    );
  }

  const customerBookings = customer.bookings
    .map((id) => bookings[id])
    .filter((b): b is NonNullable<typeof b> => Boolean(b));
  const customerDeposits = customer.deposits
    .map((id) => deposits[id])
    .filter((d): d is NonNullable<typeof d> => Boolean(d));
  const inspiration = inspirationFor(customer);
  const progress = progressFor(customer, pieces);

  return (
    <article className="flex flex-col h-full">
      {fromState?.fromCalendarStudioId ? (
        <BackLink
          className="px-6 pt-4"
          label={`Back to ${fromState.fromCalendarStudioId.replace('-', ' ')} calendar`}
          onClick={() =>
            navigate(`/calendar/${fromState.fromCalendarStudioId}`, {
              state: fromState.fromCalendarDayIso
                ? { focusDayIso: fromState.fromCalendarDayIso }
                : undefined,
            })
          }
        />
      ) : (
        <BackLink className="px-6 pt-4" />
      )}
      <CustomerRecordHeader customer={customer} editable={Boolean(storedCustomer)} />

      <div className="grid grid-cols-12 flex-1 min-h-0">
        {/* LEFT: everything we know about the customer */}
        <aside className="col-span-3 border-r border-gold-dim/15 overflow-y-auto">
          <div className="px-6 py-6 space-y-6">
            <Section label="Source">
              <p className="font-serif text-base text-cream capitalize">
                {customer.source.channel.replace('-', ' ')}
              </p>
              {customer.source.campaignId ? (
                <p className="font-mono text-xs text-cream-soft mt-1">
                  {customer.source.campaignId}
                </p>
              ) : null}
              <p className="font-inter text-[11px] text-cream-faint uppercase tracking-wider mt-1">
                First touch · {simTimeFull(customer.source.firstTouchAt)}
              </p>
            </Section>

            {/* Interactive brief + preferences (shared with My Queue): style /
                artist / studio scope the availability pane on the right. */}
            <CustomerBriefControls customer={customer} idPrefix="customer-brief" />

            <Section
              label="Medical"
              action={
                canEditMedical
                  ? { label: 'Edit medical profile', onClick: () => setMedicalOpen(true) }
                  : undefined
              }
            >
              {customer.medicalProfile && !medicalIsEmpty(customer.medicalProfile) ? (
                <div data-testid="customer-medical">
                  <dl className="space-y-2">
                    {customer.medicalProfile.allergies?.length ? (
                      <MedicalRow label="Allergies" values={customer.medicalProfile.allergies} tone="blood" />
                    ) : null}
                    {customer.medicalProfile.conditions?.length ? (
                      <MedicalRow label="Conditions" values={customer.medicalProfile.conditions} tone="blood" />
                    ) : null}
                    {customer.medicalProfile.medications?.length ? (
                      <MedicalRow label="Medications" values={customer.medicalProfile.medications} tone="cream" />
                    ) : null}
                  </dl>
                  {customer.medicalProfile.notes ? (
                    <p className="font-serif text-sm text-cream-soft mt-3 leading-relaxed">
                      {customer.medicalProfile.notes}
                    </p>
                  ) : null}
                  <p className="font-inter text-[10px] text-cream-faint uppercase tracking-wider mt-3">
                    Updated {simTimeFull(customer.medicalProfile.lastUpdatedAt)} ·{' '}
                    {customer.medicalProfile.lastUpdatedBy}
                  </p>
                </div>
              ) : (
                <p className="font-serif text-sm text-cream-soft" data-testid="customer-medical-empty">
                  No medical profile on file.
                </p>
              )}
            </Section>

            <Section label="Lifetime spend">
              <p className="font-cinzel text-2xl text-gold-bright" data-testid="customer-lifetime-spend">
                {aud(lifetimeSpend)}
              </p>
            </Section>

            {customer.creditOnFile.balanceAud > 0 ? (
              <Section label="Credit on file">
                <p className="font-cinzel text-xl text-mint">
                  {aud(customer.creditOnFile.balanceAud)}
                </p>
                {customer.creditOnFile.expiryDate ? (
                  <p className="font-inter text-[11px] text-cream-faint uppercase tracking-wider mt-1">
                    Expires {simTimeFull(customer.creditOnFile.expiryDate)}
                  </p>
                ) : null}
              </Section>
            ) : null}

            {customer.crossStudioTouches.length > 0 ? (
              <Section label="Cross-studio">
                <div className="flex flex-wrap gap-1.5">
                  {customer.crossStudioTouches.map((s) => (
                    <span key={s} className="pill-cream text-[11px]">
                      {s.replace('-', ' ')}
                    </span>
                  ))}
                </div>
              </Section>
            ) : null}

            {inspiration.length > 0 ? (
              <Section label="Reference photos on file">
                <PhotoGallery
                  photos={inspiration.map((p) => ({ url: p.url }))}
                  size="thumb"
                  density="grid"
                  testid="customer-inspiration"
                />
              </Section>
            ) : null}

            {progress.length > 0 ? (
              <Section label="Work so far">
                <PhotoGallery
                  photos={progress.map((p) => ({
                    url: p.url,
                    caption: p.caption ?? `Captured ${simTimeFull(p.capturedAt)}`,
                  }))}
                  size="thumb"
                  density="grid"
                  testid="customer-progress"
                />
              </Section>
            ) : null}

            <div className="divider-gold" />

            <Section label="Bookings">
              {customerBookings.length === 0 ? (
                <p className="font-serif text-sm text-cream-soft">No bookings yet.</p>
              ) : (
                <ul className="space-y-2">
                  {customerBookings.map((b) => {
                    const a = artists[b.artistId];
                    const price = a ? bookingPriceAud(b, a) : undefined;
                    const rebookChip = b.rebookOutcome
                      ? formatRebookOutcome(b.rebookOutcome, b.rebookAttribution, artists, agents)
                      : null;
                    // Inline structured detail (mirrors the calendar's detail card).
                    const dep = b.depositId ? deposits[b.depositId] : undefined;
                    // B3: who approved a no-deposit booking (the "approved by Ops" stamp).
                    const approval = b.depositWaived ? approvedOverrideForBooking(b.id, overrideRequests) : undefined;
                    const waiver = Object.values(waivers)
                      .filter((w) => w.bookingId === b.id)
                      .sort((x, y) => y.createdAt.localeCompare(x.createdAt))[0];
                    const waiverChipLabel =
                      !waiver || waiver.status === 'not-sent'
                        ? 'Send waiver'
                        : waiver.status === 'signed'
                          ? 'Waiver signed'
                          : 'Waiver sent';
                    // ONE balance rule (tv-2na.8.21): the deposit before checkout, the sale after.
                    const money = bookingMoney(b, dep, salePaymentList);
                    const paid = money.paidAud;
                    const dayRateText = b.dayRateAud != null ? aud(b.dayRateAud) : null;
                    const balance = b.status === 'cancelled' || b.status === 'no-show' ? null : money.balanceAud;
                    const flags = computeBookingFlags(b, customer, dep, Object.values(bookings));
                    const placementLabel = formatBodyPart(b);
                    const piece = b.pieceId ? pieces[b.pieceId] : undefined;
                    let sessionNo: number | null = null;
                    if (piece) {
                      const linked = piece.bookingsLinked
                        .map((id) => bookings[id])
                        .filter(Boolean)
                        .sort((x, y) => (x!.start < y!.start ? -1 : 1));
                      const idx = linked.findIndex((x) => x!.id === b.id);
                      sessionNo = idx >= 0 ? idx + 1 : (piece.sessionsCompleted ?? linked.length) + 1;
                    }
                    const hasDetail =
                      flags.length > 0 ||
                      dayRateText != null ||
                      paid > 0 ||
                      !!dep?.ledger?.length ||
                      !!placementLabel ||
                      !!b.refsLocation ||
                      !!piece;
                    return (
                      <li
                        key={b.id}
                        onClick={() => setEditBookingId(b.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setEditBookingId(b.id);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        className="panel-dark p-3 cursor-pointer hover:border-gold-bright/60 focus-visible:border-gold-bright transition"
                        data-testid={`customer-booking-${b.id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-serif text-base text-cream">
                            {a?.displayName ?? b.artistId} · {b.studioId.replace('-', ' ')}
                          </p>
                          {price !== undefined ? (
                            <span className="font-inter text-base text-gold-bright shrink-0">
                              {aud(price)}
                            </span>
                          ) : null}
                        </div>
                        <p className="font-mono text-xs text-cream-soft mt-1">
                          {simTimeFull(b.start)} · {b.sessionLength.replace('-', ' ')}
                        </p>
                        <div className="flex flex-wrap gap-1.5 mt-2 items-center">
                          <p
                            className="pill-cream text-[11px]"
                            data-testid={`customer-booking-status-${b.id}`}
                          >
                            {lifecycleLabel(b)}
                          </p>
                          {rebookChip ? (
                            <span
                              data-testid={`customer-booking-rebook-${b.id}`}
                              className={clsx(
                                'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-inter uppercase tracking-widest border',
                                rebookChip.tone === 'mint' && 'border-mint/50 bg-mint/10 text-mint',
                                rebookChip.tone === 'blood' && 'border-blood/50 bg-blood/10 text-blood',
                                rebookChip.tone === 'gold' && 'border-gold-bright/50 bg-gold/10 text-gold-bright',
                              )}
                            >
                              {rebookChip.label}
                            </span>
                          ) : null}
                          {approval ? (
                            <span
                              data-testid={`customer-booking-approved-${b.id}`}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-inter uppercase tracking-widest border border-mint/50 bg-mint/10 text-mint"
                            >
                              No deposit · {approvedByOpsLabel(approval)}
                            </span>
                          ) : null}
                          {b.status === 'held' && dep?.status === 'pending' ? (
                            <span
                              data-testid={`customer-booking-payby-${b.id}`}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-inter uppercase tracking-widest border border-gold-bright/50 bg-gold/10 text-gold-bright"
                            >
                              Deposit link · pay by {simTimeFull(dep.expiresAt)}
                            </span>
                          ) : null}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setWaiverBookingId(b.id);
                            }}
                            data-testid={`customer-booking-waiver-${b.id}`}
                            className={clsx(
                              'ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-inter uppercase tracking-widest border transition',
                              waiver?.status === 'signed'
                                ? 'border-mint/50 text-mint hover:bg-mint/10'
                                : 'border-gold-dim/40 text-gold-bright hover:border-gold-bright hover:bg-gold/10',
                            )}
                          >
                            <ShieldCheck size={10} /> {waiverChipLabel}
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditBookingId(b.id)}
                            data-testid={`customer-booking-edit-${b.id}`}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-inter uppercase tracking-widest border border-gold-dim/40 text-gold-bright hover:border-gold-bright hover:bg-gold/10 transition"
                          >
                            <Pencil size={10} /> Edit
                          </button>
                        </div>

                        {hasDetail ? (
                          <div
                            className="mt-2 space-y-1.5 border-t border-gold-dim/15 pt-2"
                            data-testid={`customer-booking-detail-${b.id}`}
                          >
                            {flags.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {flags.map((f) => (
                                  <span
                                    key={f.kind}
                                    title={f.detail}
                                    className={clsx(
                                      'rounded-full border px-2 py-0.5 text-[10px]',
                                      FLAG_STYLE[f.kind].cls,
                                      f.detail && 'cursor-help',
                                    )}
                                  >
                                    {FLAG_STYLE[f.kind].label}
                                  </span>
                                ))}
                              </div>
                            ) : null}
                            {dayRateText != null || paid > 0 ? (
                              <p className="font-inter text-[10px] uppercase tracking-widest text-cream-soft">
                                {dayRateText != null ? (
                                  <>
                                    day rate <span className="text-cream">{dayRateText}</span>
                                  </>
                                ) : null}
                                {paid > 0 ? (
                                  <>
                                    {' · '}paid <span className="text-mint">{aud(paid)}</span>
                                  </>
                                ) : null}
                                {balance != null ? (
                                  <>
                                    {' · '}bal <span className="text-gold-bright">{aud(balance)}</span>
                                  </>
                                ) : null}
                              </p>
                            ) : null}
                            {dep?.ledger && dep.ledger.length > 0 ? (
                              <DepositMovementDrawer deposit={dep} />
                            ) : null}
                            {placementLabel || b.refsLocation ? (
                              <p className="font-serif text-[12px] text-cream-soft">
                                {placementLabel}
                                {placementLabel && b.refsLocation ? ' · ' : ''}
                                {b.refsLocation ? `refs: ${REFS_LOCATION_LABEL[b.refsLocation]}` : ''}
                              </p>
                            ) : null}
                            {piece ? (
                              <p className="font-inter text-[10px] uppercase tracking-widest text-gold-bright">
                                Session {sessionNo}
                                {piece.estimatedTotalSessions ? ` of ${piece.estimatedTotalSessions}` : ''}
                                {' · '}
                                {piece.title || piece.style || piece.designType.replace(/-/g, ' ')}
                              </p>
                            ) : null}
                          </div>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              )}
            </Section>

            <Section label="Deposits">
              {customerDeposits.length === 0 ? (
                <p className="font-serif text-sm text-cream-soft">No deposits yet.</p>
              ) : (
                <ul className="space-y-2">
                  {customerDeposits.map((d) => (
                    <li
                      key={d.id}
                      className="panel-dark p-3 flex items-center justify-between"
                    >
                      <span className="font-inter text-base text-cream">
                        Agreed deposit {aud(d.amountAud)}
                      </span>
                      <span
                        className="pill-cream text-[11px]"
                        data-testid={`customer-deposit-status-${d.id}`}
                      >
                        {depositStatusLabel(d, new Date(clockIso).getTime(), d.bookingId ? bookings[d.bookingId] : undefined)}
                      </span>
                      <ResendLinkAction deposit={d} testId={`customer-deposit-resend-${d.id}`} />
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            {/* B5 (8xar): the client's self-book links, visible once sent. */}
            {(() => {
              const links = Object.values(selfBookLinks)
                .filter((l) => l.customerId === customer.id && !l.universal)
                .sort((a, b) => b.generatedAt.localeCompare(a.generatedAt));
              if (links.length === 0) return null;
              const nowMs = new Date(clockIso).getTime();
              return (
                <Section label="Self-book links">
                  <ul className="space-y-2">
                    {links.map((l) => {
                      const expired = l.status === 'expired' || (l.status !== 'paid' && new Date(l.expiresAt).getTime() <= nowMs);
                      const label =
                        l.status === 'paid'
                          ? 'Paid'
                          : expired
                            ? l.replacedByLinkId
                              ? 'Expired · replaced'
                              : 'Expired'
                            : `Sent · expires ${simTimeFull(l.expiresAt)}`;
                      return (
                        <li
                          key={l.token}
                          className="panel-dark p-3 flex items-center justify-between"
                          data-testid={`customer-self-book-link-${l.token}`}
                        >
                          <span className="font-inter text-sm text-cream">
                            {artists[l.artistId]?.displayName ?? l.artistId} · {simTimeFull(l.generatedAt)}
                          </span>
                          <span className="pill-cream text-[11px]">{label}</span>
                        </li>
                      );
                    })}
                  </ul>
                </Section>
              );
            })()}

            <Section
              label="Rapport"
              action={{ label: 'Add rapport note', onClick: () => setOpenModal('rapport') }}
            >
              {customer.rapportNotes.length === 0 ? (
                <p className="font-serif text-sm text-cream-soft">No rapport notes.</p>
              ) : (
                <ul className="space-y-2">
                  {customer.rapportNotes.map((r) => (
                    <li key={r.id} className="panel-dark p-3">
                      <p className="font-serif text-sm text-cream leading-relaxed">
                        {r.note}
                      </p>
                      <p className="font-inter text-[11px] text-cream-faint mt-2 uppercase tracking-wider">
                        {r.author} · {simTimeFull(r.timestamp)}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section
              label="Future-readiness"
              action={{
                label: 'Tag future-readiness',
                onClick: () => setOpenModal('future'),
              }}
            >
              {customer.futureReadinessSignals.length === 0 ? (
                <p className="font-serif text-sm text-cream-soft">No signals captured.</p>
              ) : (
                <ul className="space-y-2">
                  {customer.futureReadinessSignals.map((sig) => (
                    <li key={sig.id} className="panel-dark p-3">
                      <p className="font-inter text-[11px] text-gold uppercase tracking-wider">
                        {simTimeFull(sig.scheduledFor)}
                      </p>
                      <p className="font-serif text-base text-cream mt-1 leading-relaxed">
                        &ldquo;{sig.rawPhrase}&rdquo;
                      </p>
                      <span className="pill-cream text-[11px] mt-2">{sig.status}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          </div>
        </aside>

        {/* CENTRE: conversation thread + composer */}
        <section className="col-span-6 flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto px-8 py-6">
            {callForThisCustomer ? <LiveCallBanner call={callForThisCustomer} /> : null}
            <ConversationThread customer={customer} />
          </div>
          <CustomerComposer customer={customer} host="customer-record" onOpenModal={setOpenModal} />
        </section>

        {/* RIGHT: artist availability + grounded-deposit pane (same behaviour as My Queue) */}
        <aside className="col-span-3 border-l border-gold-dim/15 overflow-y-auto px-5 py-6">
          <SlotDepositPane customer={customer} />
        </aside>
      </div>

      <CustomerActionModals
        customer={customer}
        open={openModal}
        onClose={() => setOpenModal(null)}
      />

      {editBookingId ? (
        <EditBookingModal
          open
          bookingId={editBookingId}
          onClose={() => setEditBookingId(null)}
        />
      ) : null}

      {waiverBookingId && bookings[waiverBookingId] ? (
        <WaiverModal
          open
          booking={bookings[waiverBookingId]!}
          onClose={() => setWaiverBookingId(null)}
        />
      ) : null}

      {canEditMedical ? (
        <MedicalProfileModal
          open={medicalOpen}
          onClose={() => setMedicalOpen(false)}
          customer={customer}
          updatedBy={actorName}
        />
      ) : null}

      <p className="sr-only">Customer record for {customerName(customer)}.</p>
    </article>
  );
}

function Section({
  label,
  action,
  children,
}: {
  label: string;
  action?: { label: string; onClick: () => void };
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="label-small-caps">{label}</p>
        {action ? (
          <button
            type="button"
            onClick={action.onClick}
            className="text-cream-soft hover:text-gold-bright transition"
            aria-label={action.label}
            data-testid={`add-${label.toLowerCase().replace(/\s+/g, '-')}`}
            title={action.label}
          >
            <Plus size={13} />
          </button>
        ) : null}
      </div>
      {children}
    </div>
  );
}

function MedicalRow({
  label,
  values,
  tone,
}: {
  label: string;
  values: string[];
  tone: 'blood' | 'cream';
}) {
  return (
    <div>
      <dt className="font-inter text-[10px] text-cream-faint uppercase tracking-wider">{label}</dt>
      <dd className="mt-1 flex flex-wrap gap-1.5">
        {values.map((v) => (
          <span
            key={v}
            className={clsx(
              'rounded-full border px-2 py-0.5 text-[11px] capitalize',
              tone === 'blood'
                ? 'border-blood/50 bg-blood/15 text-blood'
                : 'border-gold-dim/40 text-cream-soft',
            )}
          >
            {v}
          </span>
        ))}
      </dd>
    </div>
  );
}
